// src/components/dashboard/GalleryTab.jsx
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; // Adicione esta importação
import {
    Box,
    Button,
    Flex,
    Grid,
    Heading,
    Text,
    VStack,
    useToast,
    Skeleton,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    useBreakpointValue,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    HStack,
} from '@chakra-ui/react';
import { FiPlus, FiBook } from 'react-icons/fi'; // Adicione FiBook
import ImageCard from './ImageCard';
import { supabase } from '../../config/supabaseClient';

// Componente principal da galeria
const GalleryTab = ({ images = [], onCreateNew, onImagesUpdate, refreshImageUrl }) => {
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const cancelRef = useRef();
    const navigate = useNavigate(); // Adicione esta linha

    // Valores responsivos
    const headingSize = useBreakpointValue({ base: "md", md: "lg" });
    const buttonSize = useBreakpointValue({ base: "sm", md: "md" });
    const gridColumns = useBreakpointValue({
        base: "repeat(1, 1fr)",
        sm: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
        lg: "repeat(3, 1fr)"
    });

    // Função para ir para o criador de álbum
    const goToAlbumCreator = () => {
        navigate('/album-creator');
    };

    const handleDownloadImage = async (imageUrl, imageName) => {
        try {
            // Verificar se a URL é válida
            if (!imageUrl || typeof imageUrl !== 'string') {
                throw new Error('URL da imagem inválida');
            }

            setIsLoading(true);

            const response = await fetch(imageUrl);

            if (!response.ok) {
                throw new Error(`Erro HTTP: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = imageName || 'imagem.png';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast({
                title: 'Download iniciado',
                description: `Baixando ${imageName}`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Erro ao baixar imagem:', error);
            setError(error.message);

            toast({
                title: 'Erro ao baixar',
                description: error.message || 'Ocorreu um erro ao baixar a imagem.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Função para confirmar exclusão
    const confirmDelete = (image) => {
        setImageToDelete(image);
        setIsAlertOpen(true);
    };

    // Função para excluir uma imagem (tanto original quanto gerada)
    const deleteImage = async () => {
        if (!imageToDelete) return;

        try {
            setIsDeleting(true);

            // 1. Extrair os caminhos das URLs para excluir do storage
            const originalPath = extractPathFromUrl(imageToDelete.original_image_url);
            const generatedPath = extractPathFromUrl(imageToDelete.generated_image_url);

            // 2. Excluir a imagem original do storage
            if (originalPath) {
                const { error: originalDeleteError } = await supabase.storage
                    .from('original-images')
                    .remove([originalPath]);

                if (originalDeleteError) {
                    console.error('Erro ao excluir imagem original:', originalDeleteError);
                }
            }

            // 3. Excluir a imagem gerada do storage
            if (generatedPath) {
                const { error: generatedDeleteError } = await supabase.storage
                    .from('generated-images')
                    .remove([generatedPath]);

                if (generatedDeleteError) {
                    console.error('Erro ao excluir imagem gerada:', generatedDeleteError);
                }
            }

            // 4. Excluir o registro do banco de dados
            const { error: dbDeleteError } = await supabase
                .from('images')
                .delete()
                .eq('id', imageToDelete.id);

            if (dbDeleteError) {
                throw dbDeleteError;
            }

            // 5. Notificar sucesso
            toast({
                title: 'Imagem excluída',
                description: 'A imagem foi excluída com sucesso.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // 6. Atualizar a galeria
            if (onImagesUpdate) {
                onImagesUpdate(); // Chamar a função de atualização da galeria
            }

        } catch (error) {
            console.error('Erro ao excluir imagem:', error);

            toast({
                title: 'Erro na exclusão',
                description: error.message || 'Ocorreu um erro ao excluir a imagem.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsDeleting(false);
            setImageToDelete(null);
            setIsAlertOpen(false);
        }
    };

    // Função auxiliar para extrair o caminho do arquivo da URL do Supabase
    const extractPathFromUrl = (url) => {
        if (!url) return null;

        try {
            // Tentar extrair o caminho da URL
            const matches = url.match(/\/storage\/v1\/object\/public\/[^/]+\/([^?]+)/);
            if (matches && matches[1]) {
                return decodeURIComponent(matches[1]);
            }

            // Tentar extrair o caminho da URL assinada
            const signedMatches = url.match(/\/storage\/v1\/object\/sign\/[^/]+\/([^?]+)/);
            if (signedMatches && signedMatches[1]) {
                return decodeURIComponent(signedMatches[1]);
            }

            return null;
        } catch (error) {
            console.error('Erro ao extrair caminho da URL:', error);
            return null;
        }
    };

    // Renderizar estado de carregamento
    if (isLoading) {
        return (
            <Box>
                <Flex
                    direction={{ base: "column", sm: "row" }}
                    justify="space-between"
                    align={{ base: "flex-start", sm: "center" }}
                    mb={{ base: 4, sm: 6 }}
                    gap={3}
                >
                    <Heading size={headingSize} color="gray.700" mb={{ base: 2, sm: 0 }}>
                        Sua galeria de desenhos
                    </Heading>

                    <HStack spacing={3}>
                        {/* Botão Criar Desenho */}
                        <Button
                            colorScheme="blue"
                            bgGradient="linear(to-r, brand.primary, brand.secondary)"
                            color="white"
                            leftIcon={<FiPlus />}
                            onClick={onCreateNew}
                            size={buttonSize}
                            width={{ base: "full", sm: "auto" }}
                            _hover={{
                                transform: 'translateY(-2px)',
                                boxShadow: 'md',
                            }}
                        >
                            Criar novo desenho
                        </Button>
                        
                        {/* Novo botão para criar livro de colorir */}
                        <Button
                            colorScheme="purple"
                            bgGradient="linear(to-r, purple.400, purple.600)"
                            color="white"
                            leftIcon={<FiBook />}
                            onClick={goToAlbumCreator}
                            size={buttonSize}
                            width={{ base: "full", sm: "auto" }}
                            _hover={{
                                transform: 'translateY(-2px)',
                                boxShadow: 'md',
                            }}
                        >
                            Criar Livro
                        </Button>
                    </HStack>
                </Flex>
                <Grid
                    templateColumns={gridColumns}
                    gap={{ base: 4, md: 6 }}
                >
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Skeleton key={i} height="300px" borderRadius="lg" />
                    ))}
                </Grid>
            </Box>
        );
    }

    // Renderizar estado de erro
    if (error) {
        return (
            <Box>
                <Flex
                    direction={{ base: "column", sm: "row" }}
                    justify="space-between"
                    align={{ base: "flex-start", sm: "center" }}
                    mb={{ base: 4, sm: 6 }}
                    gap={3}
                >
                    <Heading size={headingSize} color="gray.700" mb={{ base: 2, sm: 0 }}>
                        Sua galeria de desenhos
                    </Heading>
                    <HStack spacing={3}>
                        <Button
                            colorScheme="blue"
                            bgGradient="linear(to-r, brand.primary, brand.secondary)"
                            color="white"
                            leftIcon={<FiPlus />}
                            onClick={onCreateNew}
                            size={buttonSize}
                            width={{ base: "full", sm: "auto" }}
                        >
                            Criar novo desenho
                        </Button>

                        {/* Novo botão para criar livro de colorir */}
                        <Button
                            colorScheme="purple"
                            bgGradient="linear(to-r, purple.400, purple.600)"
                            color="white"
                            leftIcon={<FiBook />}
                            onClick={goToAlbumCreator}
                            size={buttonSize}
                            width={{ base: "full", sm: "auto" }}
                            _hover={{
                                transform: 'translateY(-2px)',
                                boxShadow: 'md',
                            }}
                        >
                            Criar Livro
                        </Button>
                    </HStack>
                </Flex>
                <Alert status="error" borderRadius="lg">
                    <AlertIcon />
                    <AlertTitle>Erro ao carregar imagens!</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Flex
                direction={{ base: "column", sm: "row" }}
                justify="space-between"
                align={{ base: "flex-start", sm: "center" }}
                mb={{ base: 4, sm: 6 }}
                gap={3}
            >
                <Heading size={headingSize} color="gray.700" mb={{ base: 2, sm: 0 }}>
                    Sua galeria de desenhos
                </Heading>

                <HStack spacing={3} width={{ base: "100%", sm: "auto" }}>
                    {/* Botão Criar Desenho */}
                    <Button
                        colorScheme="blue"
                        bgGradient="linear(to-r, brand.primary, brand.secondary)"
                        color="white"
                        leftIcon={<FiPlus />}
                        onClick={onCreateNew}
                        size={buttonSize}
                        width={{ base: "100%", sm: "auto" }}
                        _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: 'md',
                        }}
                    >
                        Criar novo desenho
                    </Button>
                    
                    {/* Novo botão para criar livro de colorir */}
                    <Button
                        colorScheme="purple"
                        bgGradient="linear(to-r, purple.400, purple.600)"
                        color="white"
                        leftIcon={<FiBook />}
                        onClick={goToAlbumCreator}
                        size={buttonSize}
                        width={{ base: "100%", sm: "auto" }}
                        _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: 'md',
                        }}
                    >
                        Criar Livro
                    </Button>
                </HStack>
            </Flex>

            {images.length === 0 ? (
                <VStack spacing={4} py={12} px={6} bg="white" borderRadius="lg" boxShadow="sm">
                    <Box as="svg" width="200px" height="200px" viewBox="0 0 24 24" fill="none">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" fill="#A5B4E8" />
                        <path d="M14.5 11l-3 3.5-2-2.5L7 15h10l-2.5-4z" fill="#B8E8C7" />
                    </Box>
                    <Heading size="md" color="gray.600">
                        Sua galeria está vazia
                    </Heading>
                    <Text color="gray.500" textAlign="center">
                        Crie seu primeiro desenho para colorir transformando suas fotos favoritas.
                    </Text>
                    <Button
                        onClick={onCreateNew}
                        colorScheme="blue"
                        bgGradient="linear(to-r, brand.primary, brand.secondary)"
                        color="white"
                        leftIcon={<FiPlus />}
                        mt={4}
                        size="lg"
                        width={{ base: "full", sm: "auto" }}
                        _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: 'md',
                        }}
                    >
                        Criar primeiro desenho
                    </Button>
                </VStack>
            ) : (
                <Grid
                    templateColumns={gridColumns}
                    gap={{ base: 4, md: 6 }}
                >
                    {images.map((image) => (
                        <ImageCard
                            key={image.id}
                            image={image}
                            onDownload={handleDownloadImage}
                            onDelete={() => confirmDelete(image)}
                            refreshImageUrl={refreshImageUrl}
                        />
                    ))}
                </Grid>
            )}

            {/* Diálogo de confirmação para exclusão de imagem */}
            <AlertDialog
                isOpen={isAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => setIsAlertOpen(false)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Excluir Imagem
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Tem certeza que deseja excluir esta imagem? Esta ação não pode ser desfeita.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setIsAlertOpen(false)}>
                                Cancelar
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={deleteImage}
                                ml={3}
                                isLoading={isDeleting}
                                loadingText="Excluindo..."
                            >
                                Excluir
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default GalleryTab;